/* heon-pdp.js: the Heon product page, owned by the pdp team (2026-09-26). Loaded on product templates by snippets/heon-head.liquid.
   Only acts on markup from the main-product setting "heon_layout" (.hp), so other product templates are untouched.
   1. the option legend names the choice as soon as it is made (the theme re-renders it from the server a moment later)
   2. the size guides (<dialog id="hp-guide-length"> and "hp-guide-ring", snippets/heon-pdp-guides.liquid): open, preview, choose.
      A row is previewed on hover only after the pointer really moves, so a pointer resting where the dialog opens can't mark a
      length that isn't chosen (desktop audit P2-12)
   3. desktop thumbnail column: keeps the active thumbnail in view
   4. phones: a thin position line under the photo slider
   Round 2 (setting "heon_v2", class .hp2):
   5. a choice picks the variant for all the options chosen (the server marks the default choice; this keeps it exact when a
      ring's size is picked after its metal). Reads <script data-hp-variants> next to the picker.
   6. while a ring has no size, the sticky bar's button takes you to the sizes
   7. gallery metal groups: now in assets/heon-swatch.js (loaded by snippets/heon-pdp-gallery.liquid, also in the quick shop)
   8. recommendations: pieces of the same chain family are dropped (phone audit P3-15); CSS shows the first four
   Round 3 (setting "heon_v3", class .hp3, pdp3 team 2026-09-26):
   9. the big-photo layout: the sticky buy column, a last photo alone in its row, a metal change brings the first photo into
      view, photos placed again on resize (window.hpFit, inline in snippets/heon-pdp-gallery.liquid), the sticky
      add-to-cart bar while the column's own button is out of view
   The theme swaps the option block on every variant change, so everything listens on document or re-finds its elements. */
(() => {
  const REDUCE = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* 1. legend value */
  document.addEventListener('change', e => {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('.hp .js-option')) return;
    const v = input.closest('fieldset')?.querySelector('.hp-legend__v');
    if (v && input.value) { v.textContent = input.value; v.classList.remove('hp-legend__v--open'); }
  });

  /* 5. the variant for everything chosen (capture: runs before the theme's picker reads data-variant-id) */
  document.addEventListener('change', e => {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('.js-option[data-value-id]')) return;
    const picker = input.closest('variant-picker');
    const data = picker && picker.parentElement && [...picker.parentElement.children].find(el => el.matches('script[data-hp-variants]'));
    if (!data) return;
    let map;
    try { map = JSON.parse(data.textContent); } catch (err) { return; }
    const chosen = [...picker.querySelectorAll('.option-selector:not(.option-selector--custom)')]
      .map(f => { const c = f.querySelector('input:checked'); return c ? c.value : null; });
    if (chosen.includes(null)) return;
    const hit = map.find(v => chosen.every((c, i) => v[i + 1] === c));
    if (hit) input.dataset.variantId = String(hit[0]);
  }, true);

  /* 2. size guides */
  const fieldFor = row => {
    const name = row?.dataset.option;
    return [...document.querySelectorAll('.hp fieldset.option-selector')].find(f => f.dataset.option === name) || null;
  };
  const chosenValue = row => fieldFor(row)?.querySelector('.js-option:checked')?.value || '';
  const hasDrawing = dlg => !!dlg.querySelector('.hp-guide__chains');
  function show(dlg, band) {
    dlg.querySelectorAll('[data-band]').forEach(el => {
      if (el.matches('path, text')) el.classList.toggle('is-on', el.dataset.band === String(band));
    });
  }
  const fallback = dlg => dlg.querySelector('.hp-len[aria-pressed="true"]') || dlg.querySelector('.hp-len[data-band="18"]') || dlg.querySelector('.hp-len');
  function sync(dlg) {
    const rows = [...dlg.querySelectorAll('.hp-len')];
    const current = rows.length ? chosenValue(rows[0]) : '';
    let on = null;
    rows.forEach(r => { const hit = r.dataset.value === current; r.setAttribute('aria-pressed', hit ? 'true' : 'false'); r.classList.remove('is-preview'); if (hit) on = r; });
    if (hasDrawing(dlg)) show(dlg, fallback(dlg)?.dataset.band);
    return on;
  }
  let opener = null, openName = '', armed = false, openAt = null, lastPt = null;
  document.addEventListener('pointermove', e => {
    lastPt = [e.clientX, e.clientY];
    if (!armed && (!openAt || Math.hypot(e.clientX - openAt[0], e.clientY - openAt[1]) > 4)) armed = true;
  }, { passive: true });
  function openGuide(btn, name) {
    const dlg = document.getElementById(`hp-guide-${name}`);
    if (!dlg || typeof dlg.showModal !== 'function') return false;
    opener = btn; openName = name; armed = false; openAt = lastPt;
    const on = sync(dlg);
    dlg.showModal();
    document.documentElement.classList.add('hp-lock');
    (on || dlg.querySelector('.hp-len') || dlg.querySelector('[data-hp-close]'))?.focus({ preventScroll: true });
    return true;
  }
  function closeGuide(dlg) { if (dlg?.open) dlg.close(); }
  document.addEventListener('click', e => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const open = t.closest('.hp [data-hp-guide]');
    if (open) {
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.button) return;   // a new tab: let the link work
      if (openGuide(open, open.dataset.hpGuide)) e.preventDefault();
      return;
    }
    const dlg = t.closest('.hp-guide');
    if (!dlg) return;
    if (t === dlg) { closeGuide(dlg); return; }            // a click on the backdrop
    if (t.closest('[data-hp-close]')) { closeGuide(dlg); return; }
    const row = t.closest('.hp-len');
    if (row) {
      const field = fieldFor(row);
      const input = field && [...field.querySelectorAll('.js-option')].find(i => i.value === row.dataset.value);
      if (input && !input.checked) input.click();
      dlg.querySelectorAll('.hp-len').forEach(r => r.setAttribute('aria-pressed', r === row ? 'true' : 'false'));
      if (hasDrawing(dlg)) show(dlg, row.dataset.band);
    }
  });
  // preview on hover (once the pointer has moved) and on keyboard focus, back to the chosen length after
  const preview = e => {
    const row = e.target instanceof Element && e.target.closest('.hp-len');
    const dlg = row && row.closest('.hp-guide');
    if (!dlg) return;
    if (e.type === 'mouseover' && !armed) return;
    if (e.type === 'focusin' && !row.matches(':focus-visible')) return;
    dlg.querySelectorAll('.hp-len').forEach(r => r.classList.toggle('is-preview', r === row));
    if (hasDrawing(dlg)) show(dlg, row.dataset.band);
  };
  const unpreview = e => {
    const dlg = e.target instanceof Element && e.target.closest('.hp-guide');
    if (!dlg) return;
    const to = e.relatedTarget instanceof Element ? e.relatedTarget.closest('.hp-len') : null;
    if (to && dlg.contains(to)) return;
    dlg.querySelectorAll('.hp-len.is-preview').forEach(r => r.classList.remove('is-preview'));
    if (hasDrawing(dlg)) show(dlg, fallback(dlg)?.dataset.band);
  };
  document.addEventListener('mouseover', preview);
  document.addEventListener('focusin', preview);
  document.addEventListener('mouseout', unpreview);
  document.addEventListener('focusout', unpreview);
  document.addEventListener('close', e => {
    if (!(e.target instanceof Element) || !e.target.matches('.hp-guide')) return;
    document.documentElement.classList.remove('hp-lock');
    // the option block may have been re-rendered while the guide was open: return focus to its guide button
    const back = opener && opener.isConnected ? opener : document.querySelector(`.hp [data-hp-guide="${openName}"]`);
    back?.focus({ preventScroll: true });
    opener = null; openAt = null;
  }, true);

  /* 6. sticky bar while a size is still to choose */
  document.addEventListener('click', e => {
    const btn = e.target instanceof Element && e.target.closest('.hp2-sticky .js-atc-button');
    if (!btn) return;
    const add = document.querySelector('.hp2 .js-add-to-cart');
    if (!add || !add.disabled) return;
    const field = [...document.querySelectorAll('.hp2 variant-picker .option-selector')].find(f => !f.querySelector('input:checked'));
    if (!field) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    field.scrollIntoView({ block: 'center', behavior: REDUCE ? 'auto' : 'smooth' });
    field.querySelector('input')?.focus({ preventScroll: true });
  }, true);

  /* 3 + 4. gallery */
  function initGallery(gallery) {
    if (gallery.dataset.hpInit) return;
    gallery.dataset.hpInit = '1';
    const viewer = gallery.querySelector('.media-viewer');
    const thumbs = gallery.querySelector('.media-thumbs');

    // 3. vertical thumbnail column (desktop): the theme only scrolls thumbnails sideways
    if (thumbs) {
      new MutationObserver(() => {
        if (getComputedStyle(thumbs).flexDirection !== 'column') return;
        const btn = thumbs.querySelector('.media-thumbs__btn.is-active');
        const item = btn?.closest('.media-thumbs__item');
        if (!item) return;
        const top = item.offsetTop, bottom = top + item.offsetHeight;
        if (top < thumbs.scrollTop || bottom > thumbs.scrollTop + thumbs.clientHeight) {
          thumbs.scrollTo({ top: top - (thumbs.clientHeight - item.offsetHeight) / 2, behavior: REDUCE ? 'auto' : 'smooth' });
        }
      }).observe(thumbs, { subtree: true, attributes: true, attributeFilter: ['class'] });
    }

    // 4. phone position line
    if (!viewer) return;
    const dots = document.createElement('div');
    dots.className = 'hp-dots';
    dots.setAttribute('aria-hidden', 'true');
    gallery.querySelector('.media-gallery__viewer')?.after(dots);
    gallery.classList.add('hp-has-dots');
    const visible = () => [...viewer.children].filter(li => li.matches('.media-viewer__item') && li.offsetWidth > 0);
    let count = -1;
    function update() {
      const items = visible();
      if (items.length !== count) {
        count = items.length;
        dots.replaceChildren(...items.map(() => document.createElement('i')));
        dots.hidden = count < 2;
      }
      if (count < 2) return;
      const step = items[1].offsetLeft - items[0].offsetLeft || viewer.clientWidth;
      const index = Math.max(0, Math.min(count - 1, Math.round(viewer.scrollLeft / step)));
      [...dots.children].forEach((d, i) => d.classList.toggle('is-on', i === index));
    }
    let raf = 0;
    const later = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(update); };
    viewer.addEventListener('scroll', later, { passive: true });
    window.addEventListener('resize', later);
    // colour groups show and hide photos (media-gallery sets data-media-count)
    new MutationObserver(later).observe(gallery, { attributes: true, attributeFilter: ['data-media-count'] });
    new MutationObserver(later).observe(viewer, { childList: true });
    update();
  }
  /* 9. round 3 */
  const WIDE = matchMedia('(min-width:1024px)');
  let picked = 0;
  document.addEventListener('change', e => {
    if (e.target instanceof Element && e.target.closest('.hp3 variant-picker')) picked = performance.now();
  });
  function initR3(hp) {
    if (hp.dataset.hp3Init) return;
    hp.dataset.hp3Init = '1';
    const info = hp.querySelector('.product-info');
    const gallery = hp.querySelector('media-gallery');
    const viewer = gallery && gallery.querySelector('.media-viewer');
    const css = name => parseFloat(getComputedStyle(document.documentElement).getPropertyValue(name)) || 0;
    // a. the buy column sticks under the header when it fits in the window; when it is taller, it scrolls with the page
    //    until its end shows above the sticky add-to-cart bar and stays there (a negative top), so nothing in it is ever
    //    out of reach
    const stick = () => {
      if (!info || !WIDE.matches) { hp.style.removeProperty('--hp3-stick'); return; }
      const top = (css('--hp3-hdr') || 68) + 20;
      const barH = hp.closest('.cc-main-product')?.querySelector('sticky-atc-panel[data-show-on-scroll]')?.offsetHeight || 0;
      hp.style.setProperty('--hp3-stick', Math.round(Math.min(top, innerHeight - info.offsetHeight - 24 - barH)) + 'px');
    };
    stick();
    if (info && window.ResizeObserver) new ResizeObserver(stick).observe(info);
    // b. a last photo alone in its row spans the gallery, like the first
    const shown = () => viewer ? [...viewer.children].filter(li => li.matches('.media-viewer__item') && li.style.display !== 'none') : [];
    const solo = () => {
      const items = shown();
      items.forEach(li => li.classList.remove('hp3-solo'));
      if (items.length > 1 && items.length % 2 === 0) items[items.length - 1].classList.add('hp3-solo');
    };
    solo();
    // c. a metal chosen while the photos' top is out of view: back to the first photo (the chosen metal's), wide screens
    let first = shown()[0];
    if (viewer) new MutationObserver(() => {
      solo();
      const f = shown()[0];
      if (!f || f === first) return;
      first = f;
      if (!WIDE.matches || performance.now() - picked > 2000) return;
      const r = gallery.getBoundingClientRect();
      if (r.top < 0) scrollTo({ top: Math.max(0, r.top + scrollY - (css('--hp3-top') || 174)), behavior: REDUCE ? 'auto' : 'smooth' });
    }).observe(viewer, { childList: true });
    // d. photos placed again when the window changes size (the box changes shape at 1024px); any that loaded early
    const refit = () => { if (window.hpFit) hp.querySelectorAll('.hp3-fit .product-image').forEach(im => { if (im.complete) window.hpFit(im); }); };
    refit();
    let t = 0;
    addEventListener('resize', () => { clearTimeout(t); t = setTimeout(() => { stick(); refit(); }, 150); });
    // e. the sticky add-to-cart bar: sticky-atc-panel.js shows it once the whole buy column has left the window, which a
    //    sticky column never does; from 1024px it shows while the column's own add-to-cart button is out of view
    const sec = hp.closest('.cc-main-product');
    const bar = sec && sec.querySelector('sticky-atc-panel[data-show-on-scroll]');
    if (bar) {
      let raf = 0;
      const check = () => {
        raf = 0;
        if (!WIDE.matches) return;
        const b = sec.querySelector('product-form')?.querySelector('.js-add-to-cart');
        if (!b) return;
        const r = b.getBoundingClientRect(), seen = r.bottom > 0 && r.top < innerHeight;
        if (bar.classList.contains('sticky-atc-panel--out') !== seen) bar.classList.toggle('sticky-atc-panel--out', seen);
      };
      const later = () => { if (!raf) raf = requestAnimationFrame(check); };
      addEventListener('scroll', later, { passive: true });
      new MutationObserver(later).observe(bar, { attributes: true, attributeFilter: ['class'] });
      later();
    }
  }

  function init() {
    document.querySelectorAll('.hp3').forEach(initR3);
    document.querySelectorAll('.hp media-gallery').forEach(initGallery);
    document.querySelectorAll('.cc-product-recommendations product-recommendations').forEach(watchRecs);
  }

  /* 7. metal groups: moved to assets/heon-swatch.js (swatch team, 2026-09-26) so the quick shop on collection and search
     pages gets them too; snippets/heon-pdp-gallery.liquid loads it. */

  /* 8. recommendations: the theme fills <product-recommendations> after load */
  const FAMILY = /\b(rolo|belcher|cable|curb|paperclip|cuban|box|popcorn|rope|herringbone|ball|mariner|figaro|wheat|singapore|franco|satellite)\b/g;
  function filterRecs(root) {
    const hp = document.querySelector('.hp2[data-hp-name]');
    if (!hp) return;
    // theme i18n (2026-09-28): on a page in another language the English name (data-hh-name) and each card's handle
    const name = (hp.dataset.hhName || hp.dataset.hpName).toLowerCase();
    if (!/\bchain\b/.test(name)) return;
    const fam = name.match(FAMILY);
    if (!fam) return;
    root.querySelectorAll('ul[role="list"] > li').forEach(li => {
      const a = li.querySelector('.card__title a');
      if (!a) return;
      const n = hp.dataset.hhName ? (a.getAttribute('href') || '').split('?')[0].split('/').pop().replace(/-/g, ' ')
        : (a.querySelector('.hh-cn')?.textContent || a.textContent).toLowerCase().split(',')[0];
      if ((n.match(FAMILY) || []).some(w => fam.includes(w))) li.remove();
    });
  }
  function watchRecs(el) {
    if (el.dataset.hpWatch || !document.querySelector('.hp2')) return;
    el.dataset.hpWatch = '1';
    filterRecs(el);
    new MutationObserver(() => filterRecs(el)).observe(el, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  // the gallery is replaced when a different product loads into the page (combined listings)
  document.addEventListener('on:variant:change', () => setTimeout(init, 50));
})();
